import { formatNumber } from '@/apps/order/helper';
import { $text } from '@/utils/TextHelper';
import ProductOptionInterface from '@/apps/order/interfaces/fiber/ProductOptionInterface';
import ProductOptionProductPriceInterface from '@/apps/order/interfaces/fiber/ProductOptionProductPriceInterface';
import FiberConfigFormInterface from '@/apps/order/interfaces/fiber/FiberConfigFormInterface';

type FiberOptionsRecord = FiberConfigFormInterface['options'];

type PropertyConnectionOption = {
  value: string;
  label: string;
  price: string;
};

type FiberOptionPayloadEntry = {
  option: string;
  revision: string;
  selected: string[];
};

// Resolved selected option product for preview breakdown; `monthly` = recurring base_price, `oneTime` = one-time base_price (preview sums monthly only).
type FiberSelectedOption = {
  productUuid: string;
  optionUuid: string;
  category: string;
  optionName: string;
  productName: string;
  monthly: number;
  oneTime: number;
};

// Mirrors Ahrensburg getInternetConfigModalFormDefaults: preselect selected||default by option.uuid; scalar vs array by canSelectMultiple.
const buildDefaults = (options: ProductOptionInterface[]): FiberOptionsRecord => {
  const out: FiberOptionsRecord = {};

  options.forEach((option) => {
    const preselected = option.products
      .filter((p) => p.selected || p.default)
      .map((p) => p.productUuid);

    out[option.uuid] = option.canSelectMultiple ? [...preselected] : preselected[0];
  });

  return out;
};

// Serialise the single options Record into the one flat wire array.
const mapOptionsToPayload = (
  optionsRecord: FiberOptionsRecord,
  options: ProductOptionInterface[],
): FiberOptionPayloadEntry[] => options
  .map((option) => {
    const value = optionsRecord[option.uuid];
    let selected: string[] = [];
    if (Array.isArray(value)) {
      selected = value;
    } else if (value) {
      selected = [value];
    }

    return { option: option.uuid, revision: option.revisionUuid, selected };
  })
  .filter((entry) => entry.selected.length > 0);

// Price label uses payment kind not amount — recurring (incl. 0) → €/Monat; one-time (incl. 0) → plain €.
const formatOptionPriceLabel = (price?: ProductOptionProductPriceInterface | null): string => {
  if (!price) return '';

  const recurring = price.recurring?.[0]?.value;
  if (typeof recurring === 'number') {
    return `${formatNumber(recurring, 2)}&thinsp;€/Monat`;
  }

  const oneTime = price.oneTime?.[0]?.value;
  if (typeof oneTime === 'number') {
    return `${formatNumber(oneTime, 2)}&thinsp;€`;
  }

  return '';
};

// Recurring (monthly base_price) value; falls back to first recurring entry when no base_price type.
const getOptionRecurring = (price?: ProductOptionProductPriceInterface | null): number => {
  if (!price?.recurring?.length) return 0;
  const base = price.recurring.find((entry) => entry.type === 'base_price');
  const value = (base ?? price.recurring[0])?.value;
  return typeof value === 'number' ? value : 0;
};

// One-time (base_price) value; falls back to first one-time entry (e.g. setup_fee-only) when no base_price type.
const getOptionOneTime = (price?: ProductOptionProductPriceInterface | null): number => {
  if (!price?.oneTime?.length) return 0;
  const base = price.oneTime.find((entry) => entry.type === 'base_price');
  const value = (base ?? price.oneTime[0])?.value;
  return typeof value === 'number' ? value : 0;
};

// Flatten form selection (Record<optionUuid, productUuid|productUuid[]>) into resolved products across groups, preserving order.
const collectSelectedOptions = (
  optionsRecord: FiberOptionsRecord,
  options: ProductOptionInterface[],
): FiberSelectedOption[] => {
  const out: FiberSelectedOption[] = [];

  options.forEach((option) => {
    const value = optionsRecord[option.uuid];
    const selectedUuids = Array.isArray(value) ? value : [value].filter(Boolean) as string[];

    selectedUuids.forEach((productUuid) => {
      const product = option.products.find((entry) => entry.productUuid === productUuid);
      if (!product) return;

      out.push({
        productUuid,
        optionUuid: option.uuid,
        category: option.category,
        optionName: option.name,
        productName: product.name,
        monthly: getOptionRecurring(product.price),
        oneTime: getOptionOneTime(product.price),
      });
    });
  });

  return out;
};

// Sum recurring surcharges only — one-time prices (Servicetechniker, Bereitstellung) excluded from monthly total.
const sumSelectedMonthly = (selected: FiberSelectedOption[]): number => selected
  .reduce((sum, option) => sum + option.monthly, 0);

// Language name → inline flag SVG (no media on option product; '' when unmatched) — not emoji (Windows renders letter pairs); paths from MIT flag-icons 4x3.
const flagSvg = (inner: string): string => (
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" width="28" height="21" '
  + 'aria-hidden="true" style="border-radius:3px;display:block">'
  + inner
  + '<rect x="4" y="4" width="632" height="472" rx="48" fill="none" '
  + 'stroke="rgba(0,0,0,.15)" stroke-width="8"/>'
  + '</svg>'
);

const FLAG_TR = flagSvg(
  '<g fill-rule="evenodd">'
  + '<path fill="#e30a17" d="M0 0h640v480H0z"/>'
  + '<path fill="#fff" d="M407 247.5c0 66.2-54.6 119.9-122 119.9s-122-53.7-122-120 '
  + '54.6-119.8 122-119.8 122 53.7 122 119.9"/>'
  + '<path fill="#e30a17" d="M413 247.5c0 53-43.6 95.9-97.5 95.9s-97.6-43-97.6-96 '
  + '43.7-95.8 97.6-95.8 97.6 42.9 97.6 95.9z"/>'
  + '<path fill="#fff" d="m430.7 191.5-1 44.3-41.3 11.2 40.8 14.5-1 40.7 26.5-31.8 '
  + '40.2 14-23.2-34.1 28.3-33.9-43.5 12-25.8-37z"/>'
  + '</g>',
);
const FLAG_PL = flagSvg(
  '<g fill-rule="evenodd">'
  + '<path fill="#fff" d="M0 0h640v240H0z"/>'
  + '<path fill="#dc143c" d="M0 240h640v240H0z"/>'
  + '</g>',
);
const FLAG_RU = flagSvg(
  '<path fill="#fff" d="M0 0h640v160H0z"/>'
  + '<path fill="#0039a6" d="M0 160h640v160H0z"/>'
  + '<path fill="#d52b1e" d="M0 320h640v160H0z"/>',
);
const FLAG_IT = flagSvg(
  '<g fill-rule="evenodd" stroke-width="1pt">'
  + '<path fill="#fff" d="M0 0h640v480H0z"/>'
  + '<path fill="#009246" d="M0 0h213.3v480H0z"/>'
  + '<path fill="#ce2b37" d="M426.7 0H640v480H426.7z"/>'
  + '</g>',
);

const LANGUAGE_FLAGS: Array<{ match: string; flag: string }> = [
  { match: 'türk', flag: FLAG_TR },
  { match: 'turk', flag: FLAG_TR },
  { match: 'poln', flag: FLAG_PL },
  { match: 'russ', flag: FLAG_RU },
  { match: 'ital', flag: FLAG_IT },
];

const getLanguageFlag = (name?: string | null): string => {
  if (!name) return '';
  const lower = name.toLowerCase();
  return LANGUAGE_FLAGS.find((entry) => lower.includes(entry.match))?.flag ?? '';
};

// Hausanschluss meter cards — Ahrensburg hardcoded source for connection form + overview; 50-80m shows individual offer for business; form hides cards when isBusiness.
const buildPropertyConnectionOptions = (isBusiness = false): PropertyConnectionOption[] => {
  const individualOffer = $text('fiber.connection', 'meter.individualOffer', 'Individuelles Angebot');

  return [
    {
      value: '20m',
      label: $text('fiber.connection', 'meter.20m', 'Hausanschluss inkl. 20 Meter'),
      price: $text('fiber.connection', 'meter.20m.price', '690,00 €'),
    },
    {
      value: '20m-50m',
      label: $text('fiber.connection', 'meter.20m-50m', 'Hausanschluss inkl. 20-50 Meter'),
      price: $text('fiber.connection', 'meter.20m-50m.price', '890,00 €'),
    },
    {
      value: '50m-80m',
      label: $text('fiber.connection', 'meter.50m-80m', 'Hausanschluss inkl. 50-80 Meter'),
      price: isBusiness
        ? individualOffer
        : $text('fiber.connection', 'meter.50m-80m.price', '1.200,00 €'),
    },
    {
      value: 'individual',
      label: $text('fiber.connection', 'meter.individual', 'Hausanschluss mit über 80 Meter'),
      price: individualOffer,
    },
  ];
};

export {
  FiberOptionsRecord,
  FiberOptionPayloadEntry,
  FiberSelectedOption,
  PropertyConnectionOption,
  buildDefaults,
  mapOptionsToPayload,
  formatOptionPriceLabel,
  getOptionRecurring,
  getOptionOneTime,
  collectSelectedOptions,
  sumSelectedMonthly,
  buildPropertyConnectionOptions,
  getLanguageFlag,
};
